// CTW-DURMAH-STUDENT-PAINPOINTS-SEO-RESOURCE-EXPANSION-02:
// Student journey cluster article page: the Year 1 to Year 2 transition.
// Fully public. Content lives in the data module. Classification and
// module-sequence specifics always defer to the student's own programme
// handbook; no percentages or invented policies.

import React from "react";
import Head from "next/head";
import { RelatedVideoLessons } from "@/components/resources/RelatedVideoLessons";
import {
  secondYearLawWhatChangesSections,
  secondYearLawWhatChangesSources,
} from "@/content/resources/secondYearLawWhatChanges";
import type { ResourceSection } from "@/content/resources/types";
import {
  ResourceBreadcrumbs,
  ResourceCrossLinks,
  ResourceDisclaimer,
  ResourceHero,
  ResourceSoftCta,
  SectionSourceLinks,
  SourcesSection,
} from "@/components/resources/ResourcePrimitives";

const SITE = "https://www.durmah.ai";

function SectionBlock({ section }: { section: ResourceSection }) {
  return (
    <section id={section.id} className="mb-14 scroll-mt-32">
      <h2 className="mb-4 text-2xl md:text-3xl font-black text-gray-900 dark:text-white">
        {section.heading}
      </h2>
      {section.paragraphs.map((p, i) => (
        <p key={i} className="mb-4 leading-relaxed text-gray-600 dark:text-slate-400">
          {p}
        </p>
      ))}
      {section.bullets && (
        <ul className="mb-4 space-y-3">
          {section.bullets.map((b, i) => (
            <li key={i} className="flex gap-3 leading-relaxed text-gray-600 dark:text-slate-400">
              <span aria-hidden className="mt-[10px] h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      {section.callout && (
        <div className="mt-5 rounded-2xl border border-indigo-100 dark:border-indigo-500/20 bg-indigo-50/70 dark:bg-indigo-500/5 p-6">
          <p className="mb-1.5 text-sm font-black text-indigo-900 dark:text-indigo-300">
            {section.callout.title}
          </p>
          <p className="m-0 text-sm leading-relaxed text-indigo-900/80 dark:text-indigo-200/70">
            {section.callout.body}
          </p>
        </div>
      )}
      <SectionSourceLinks links={section.links} />
    </section>
  );
}

export default function SecondYearLawWhatChangesPage() {
  const canonical = `${SITE}/resources/second-year-law-what-changes`;
  const title = "Second Year of a Law Degree: What Changes and How to Prepare | Durmah";
  const description =
    "Is second year of a UK law degree really harder? An honest guide to what actually changes: marks that start counting, the shift from description to critical analysis, denser modules, the applications calendar collision, and a concrete first-month plan.";

  const articleSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Second Year of a Law Degree: What Changes and How to Prepare",
    description,
    url: canonical,
    isAccessibleForFree: true,
    author: { "@type": "Organization", name: "Durmah" },
    publisher: {
      "@type": "Organization",
      name: "Durmah",
      logo: { "@type": "ImageObject", url: `${SITE}/android-chrome-512x512.png` },
    },
    datePublished: "2026-07-10",
    dateModified: "2026-07-10",
  });

  const breadcrumbSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Student Resources", item: `${SITE}/resources` },
      { "@type": "ListItem", position: 2, name: "Second Year: What Changes", item: canonical },
    ],
  });

  return (
    <div className="bg-white dark:bg-[#020617] min-h-screen flex flex-col pt-32">
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link key="route-canonical" rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta key="route-og-url" property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: articleSchema }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbSchema }} />
      </Head>

      <main className="flex-1 py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ResourceBreadcrumbs current="Second Year: What Changes" />
          <ResourceHero
            eyebrow="Student journey"
            title={
              <>
                Second Year:{" "}
                <span className="text-indigo-600 dark:text-indigo-400">What Changes</span>
              </>
            }
            subtitle="Every summer, thousands of law students ask whether second year is really that much harder. The forums say yes and explain nothing. The honest answer: it is a rules change more than a difficulty spike. Marks start counting, expectations shift from describing law to arguing about it, and the calendar fills. Here is the specific picture, and the plan."
          />

          {/* In-page navigation */}
          <nav aria-label="On this page" className="mb-12 rounded-2xl border border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-900/40 p-6">
            <p className="mb-3 text-xs font-black uppercase tracking-widest text-gray-500 dark:text-slate-500">
              On this page
            </p>
            <ul className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
              {secondYearLawWhatChangesSections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-sm font-medium text-indigo-700 dark:text-indigo-400 hover:underline"
                  >
                    {s.heading}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {secondYearLawWhatChangesSections.map((section) => (
            <SectionBlock key={section.id} section={section} />
          ))}

          <RelatedVideoLessons
            heading="Move from description to argument"
            intro="Watch a short paragraph-planning check, then read the explanation and test it against your own essay question and marking criteria."
            slugs={["essay-argument-not-description", "counterargument-change-your-position"]}
          />
          <ResourceCrossLinks
            items={[
              {
                title: "The UK Law Student Guide",
                description:
                  "The stage-by-stage pillar this article deepens, including its full Year 2 strategy section.",
                href: "/resources/uk-law-student-guide",
              },
              {
                title: "Internship Readiness",
                description:
                  "The applications side of the calendar collision: CVs, cover letters and the year-by-year rhythm.",
                href: "/resources/internship-readiness",
              },
              {
                title: "Wellbeing for law students",
                description:
                  "Sustainable pacing and where to find support when the pressure is about more than the reading.",
                href: "/learn/wellbeing",
              },
              {
                title: "Preparing for Law Seminars",
                description:
                  "The weekly craft that matters most in the year the modules get denser and the marks count.",
                href: "/resources/preparing-for-law-seminars",
              },
            ]}
          />

          <SourcesSection groups={secondYearLawWhatChangesSources} />
          <ResourceSoftCta
            title="Heading into the year that counts?"
            body="Durmah keeps your lectures, modules and revision organised while the calendar fills, and trains the critical reasoning your markers now want. The thinking stays yours. This guide stays free either way."
          />
          <ResourceDisclaimer />
        </div>
      </main>
    </div>
  );
}
